import { useCallback, useEffect } from 'react';
import { setProductSearch } from '../../store/productSlice';
import { computeDiscountAmount } from '../../utils/exchangeDiscountMath';

const stockCap = (item) => {
  const q = item?.stock?.total_quantity;
  return Number.isFinite(q) ? q : Infinity;
};

export default function useDashboardReturnExchange({
  dispatch,
  exchangeNewItems,
  location,
  navigate,
  returnOrderInput,
  returnSearchTimerRef,
  setBillingMode,
  setExchangeCheckoutOpen,
  setExchangeNewItems,
  setExchangeReturnItems,
  setExchangeSourceOrder,
  setLookedUpOrder,
  setReturnCustomerOrders,
  setReturnDrawerOpenDash,
  setReturnOrderInput,
  setReturnOrderMatches,
  setReturnSearchResults,
  setReturnSearched,
  setSearchHoverIndex,
  setSelectedReturnCustomer,
  showToast,
  triggerCustomerSearch,
  triggerListReturnOrders,
  triggerSelectOrder,
}) {
  const buildExchangeSelectionMap = useCallback((order) => {
    const items = {};
    (order.items || []).forEach((item) => {
      const returnable = parseFloat(item.quantity_returnable ?? item.quantity ?? 0);
      if (returnable > 0) {
        items[item.id] = { selected: false, quantity: 1, reason_code_id: '' };
      }
    });
    return items;
  }, []);

  const handleBillingModeChange = useCallback((newMode) => {
    setBillingMode(newMode);
    dispatch(setProductSearch(''));
    setSearchHoverIndex(-1);

    if (newMode === 'billing') {
      setReturnOrderInput('');
      setLookedUpOrder(null);
      setReturnSearchResults([]);
      setReturnOrderMatches([]);
      setReturnSearched(false);
      setSelectedReturnCustomer(null);
      setReturnCustomerOrders([]);
      setExchangeSourceOrder(null);
      setExchangeReturnItems({});
      setExchangeNewItems([]);
      setExchangeCheckoutOpen(false);
    }
  }, [
    dispatch,
    setBillingMode,
    setExchangeCheckoutOpen,
    setExchangeNewItems,
    setExchangeReturnItems,
    setExchangeSourceOrder,
    setLookedUpOrder,
    setReturnCustomerOrders,
    setReturnOrderInput,
    setReturnOrderMatches,
    setReturnSearchResults,
    setReturnSearched,
    setSearchHoverIndex,
    setSelectedReturnCustomer,
  ]);

  const handleSelectExchangeOrder = useCallback(async (order) => {
    try {
      const res = await triggerSelectOrder(order.id).unwrap();
      const fullOrder = res?.data?.order || res?.data;
      if (!fullOrder) return;
      setLookedUpOrder(fullOrder);
      setExchangeSourceOrder(fullOrder);
      setExchangeReturnItems(buildExchangeSelectionMap(fullOrder));
      setExchangeNewItems([]);
    } catch {
      showToast('Could not load order details', 'error');
    }
  }, [
    buildExchangeSelectionMap,
    setExchangeNewItems,
    setExchangeReturnItems,
    setExchangeSourceOrder,
    setLookedUpOrder,
    showToast,
    triggerSelectOrder,
  ]);

  const handleCancelExchange = useCallback(() => {
    setExchangeSourceOrder(null);
    setExchangeReturnItems({});
    setExchangeNewItems([]);
    setExchangeCheckoutOpen(false);
    setLookedUpOrder(null);
  }, [
    setExchangeCheckoutOpen,
    setExchangeNewItems,
    setExchangeReturnItems,
    setExchangeSourceOrder,
    setLookedUpOrder,
  ]);

  useEffect(() => {
    const orderId = location.state?.exchangeOrderId;
    if (!orderId) return;

    // Clear via the router, not a raw window.history.replaceState — that call
    // only rewrites the underlying browser entry and never reaches react-router's
    // own history/location, so useLocation() keeps reporting the stale
    // exchangeOrderId forever (surfaces e.g. on a component remount, which sees
    // location.state as still carrying it and walks straight back into exchange
    // mode for an order the cashier already left).
    navigate(location.pathname, { replace: true, state: null });
    setBillingMode('exchange');
    triggerSelectOrder(orderId)
      .then((res) => {
        const fullOrder = res?.data?.data?.order || res?.data?.data || res?.data;
        if (!fullOrder) return;
        setLookedUpOrder(fullOrder);
        setExchangeSourceOrder(fullOrder);
        setExchangeReturnItems(buildExchangeSelectionMap(fullOrder));
        setExchangeNewItems([]);
      })
      .catch(() => showToast('Could not load order for exchange', 'error'));
  }, [
    buildExchangeSelectionMap,
    location.pathname,
    location.state,
    navigate,
    setBillingMode,
    setExchangeNewItems,
    setExchangeReturnItems,
    setExchangeSourceOrder,
    setLookedUpOrder,
    showToast,
    triggerSelectOrder,
  ]);

  const handleAddExchangeNewItem = useCallback((product) => {
    if (!product.retail_price || parseFloat(product.retail_price) <= 0) {
      showToast(`${product.name} has no price set`, 'error');
      return;
    }

    const stock = product.stock;
    if (stock && (stock.in_stock === false || stock.total_quantity === 0)) {
      showToast(`${product.name} is out of stock`, 'error');
      return;
    }

    const existing = exchangeNewItems.find((item) => item.product_id === product.id);
    const nextQty = (existing?.quantity || 0) + 1;
    if (nextQty > stockCap(product)) {
      showToast(`Only ${stockCap(product)} left for ${product.name}`, 'warning');
      return;
    }

    setExchangeNewItems((prev) => {
      if (existing) {
        return prev.map((item) => {
          if (item.product_id !== product.id) return item;
          const quantity = item.quantity + 1;
          const discount_amount = item.discount_id
            ? computeDiscountAmount(item.discount_type, item.discount_value, item.modified_value, item.retail_price, quantity)
            : item.discount_amount;
          return { ...item, quantity, discount_amount };
        });
      }
      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        sku: product.sku,
        display_barcode: product.display_barcode,
        retail_price: parseFloat(product.retail_price || 0),
        quantity: 1,
      }];
    });
    dispatch(setProductSearch(''));
  }, [dispatch, exchangeNewItems, setExchangeNewItems, showToast]);

  const handleReturnSearch = useCallback(async (rawQuery = returnOrderInput) => {
    const q = rawQuery.trim();
    if (q.length < 2) return;
    setReturnSearchResults([]);
    setReturnOrderMatches([]);
    setReturnSearched(false);
    setSelectedReturnCustomer(null);
    setReturnCustomerOrders([]);
    setLookedUpOrder(null);
    try {
      const [customerRes, orderRes] = await Promise.allSettled([
        triggerCustomerSearch({ search: q }).unwrap(),
        triggerListReturnOrders({ order_number: q, status: 'paid', per_page: 10 }).unwrap(),
      ]);
      const customers = customerRes.status === 'fulfilled' ? (customerRes.value?.data || []) : [];
      const orders = orderRes.status === 'fulfilled' ? (orderRes.value?.data?.orders || []) : [];
      setReturnSearchResults(customers);
      setReturnOrderMatches(orders);
    } catch {
      setReturnSearchResults([]);
      setReturnOrderMatches([]);
    } finally {
      setReturnSearched(true);
    }
  }, [
    returnOrderInput,
    setLookedUpOrder,
    setReturnCustomerOrders,
    setReturnOrderMatches,
    setReturnSearchResults,
    setReturnSearched,
    setSelectedReturnCustomer,
    triggerCustomerSearch,
    triggerListReturnOrders,
  ]);

  useEffect(() => {
    return () => {
      if (returnSearchTimerRef.current) clearTimeout(returnSearchTimerRef.current);
    };
  }, [returnSearchTimerRef]);

  const handleSelectReturnCustomer = useCallback(async (customer) => {
    setSelectedReturnCustomer(customer);
    setReturnCustomerOrders([]);
    try {
      const res = await triggerListReturnOrders({ customer_id: customer.id, status: 'paid', per_page: 50 }).unwrap();
      setReturnCustomerOrders(res?.data?.orders || []);
    } catch {
      setReturnCustomerOrders([]);
    }
  }, [setReturnCustomerOrders, setSelectedReturnCustomer, triggerListReturnOrders]);

  const handleSelectReturnOrder = useCallback(async (order) => {
    try {
      const res = await triggerSelectOrder(order.id).unwrap();
      const fullOrder = res?.data?.order || res?.data;
      if (!fullOrder) return;
      setLookedUpOrder(fullOrder);
      setReturnDrawerOpenDash(true);
    } catch {
      showToast('Could not load order details', 'error');
    }
  }, [setLookedUpOrder, setReturnDrawerOpenDash, showToast, triggerSelectOrder]);

  return {
    handleBillingModeChange,
    handleSelectExchangeOrder,
    handleCancelExchange,
    handleAddExchangeNewItem,
    handleReturnSearch,
    handleSelectReturnCustomer,
    handleSelectReturnOrder,
  };
}
